import { crack, figure, flames, groundRing, peak, sabre, shards, slash, speed, star } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ดาบดันผา (cinnabar, yang): a broad heavy dao, red sandstone cliffs and kiln heat. Re-themed from blood to heat
 * (10-13): the blade glows at the edge, rock cracks and pushes, embers instead of blood.
 */

const ROCK = '#b0483a';
const ROCK_LIGHT = '#d8806a';
const EMBER = '#ff7a2a';
const EMBER_HI = '#ffd27a';
const HOT = '#ffe6c0';

/** The heavy dao: dark grip, warm steel with a hot edge. */
const dao = (p: Pix, x0: number, y0: number, x1: number, y1: number, w = 4.5) =>
  sabre(p, x0, y0, x1, y1, { hilt: '#3a2620', blade: '#f0d8c8', w, edge: EMBER_HI, curve: -0.7 });

/** A red cliff face: a flat-topped crag. */
function cliff(p: Pix, x: number, by: number, w: number, h: number): void {
  peak(p, x + w / 2, by, w, h, ROCK, null);
  p.rect(Math.round(x + w * 0.3), Math.round(by - h + 2), Math.round(w * 0.4), 2, ROCK);
  p.line(x + w * 0.35, by - h * 0.6, x + w * 0.5, by - 2, ROCK_LIGHT);
}

registerIcons('skill', {
  // An endless edge: a heavy cut that walks the foe back, step marks under it.
  'rc-edge': (p) => {
    slash(p, 13, 15, 12, -2.4, 0.5, EMBER, 3);
    dao(p, 5, 27, 26, 5);
    for (let k = 0; k < 3; k++) p.rect(18 + k * 4, 28, 2, 1, EMBER_HI);
  },
  // Splitting the red cliff: the blade comes down through a cracked crag.
  'rc-split': (p) => {
    cliff(p, 4, 30, 24, 18);
    crack(
      p,
      [
        [16, 12],
        [15, 18],
        [17, 23],
        [16, 30],
      ],
      HOT,
      1,
    );
    dao(p, 22, 1, 16, 14, 4);
    shards(p, 16, 13, 8, EMBER_HI, 5, 0.3);
  },
  // A tide of broken rock rolling out of a ground cut.
  'rc-tide': (p) => {
    groundRing(p, 16, 22, 13, 6, EMBER);
    for (const [x, h] of [
      [5, 6],
      [10, 10],
      [16, 13],
      [22, 9],
      [27, 5],
    ] as const)
      cliff(p, x - 3, 24, 6, h);
    dao(p, 26, 2, 15, 18, 3.5);
  },
  // A dash behind the flat of the blade, shoving the foe.
  'rc-hunt': (p) => {
    speed(
      p,
      12,
      [
        [10, 9],
        [14, 11],
        [18, 8],
      ],
      EMBER,
    );
    dao(p, 8, 22, 26, 10, 5);
    p.rect(25, 6, 2, 18, HOT);
    p.rect(28, 9, 2, 12, EMBER_HI);
  },
  // A low sweep round the caster, rocks tumbling.
  'rc-reap': (p) => {
    groundRing(p, 16, 23, 13, 5, EMBER);
    slash(p, 16, 20, 12, 0.2, 2.9, EMBER_HI, 3);
    figure(p, 13, 9, ROCK_LIGHT, 'stand');
    for (const [x, y] of [
      [3, 14],
      [27, 13],
    ] as const)
      p.rect(x, y, 3, 2, ROCK);
  },
  // Standing on the red cliff: the dao planted in the crag.
  'rc-cliff': (p) => {
    cliff(p, 2, 30, 28, 14);
    dao(p, 16, 2, 16, 22, 4);
    p.rect(9, 18, 14, 1, EMBER_HI);
  },
  // Warmth flowing back: embers rise from a kiln glow into a heart of heat.
  'rc-drink': (p) => {
    flames(p, 6, 30, 20, 8, EMBER, EMBER_HI);
    p.ell(16, 12, 6, 6, EMBER);
    p.ell(16, 12, 3.5, 3.5, EMBER_HI);
    p.ell(16, 12, 1.5, 1.5, HOT);
    for (const [x, y] of [
      [8, 20],
      [24, 19],
      [11, 4],
      [22, 3],
    ] as const)
      p.px(x, y, EMBER_HI);
  },
  // The red cliff falls into the sea: a crag toppling into a wave, the blade above.
  'rc-sea': (p) => {
    cliff(p, 3, 25, 22, 18);
    p.rect(2, 25, 28, 5, '#4a7aa8');
    for (let x = 2; x < 30; x += 6) p.ring(x + 3, 27, 3, 2, '#bfe0f0', Math.PI, Math.PI * 2);
    dao(p, 4, 6, 28, 3, 4);
    star(p, 26, 14, 3, HOT);
    p.px(8, 26, C.white).px(22, 27, C.white);
  },
});
